import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import { CommissionContract } from "../../../api/ComissionContractsAPI";
import SectionTitle from "../../SectionTitle/SectionTitle";
import InputShortText from "../../InputShortText/InputShortText";
import InputNumber from "../../InputNumber/InputNumber";
import TextArea from "../../TextArea/TextArea";
import PriceField from "../../PriceField/PriceField";
import Switch from "../../Switch/Switch";
import CONFIG from "../../../config/config";
import FilesTabSection from "./FilesTabSection";

interface ItemTabSectionProps {
  form: CommissionContract;
  commission: CommissionContract;
  isEditing: boolean;
  showNotes: boolean;
  showDamagesNotes: boolean;
  isUploading: boolean;
  onToggleNotes: () => void;
  onToggleDamagesNotes: () => void;
  onInputChange: (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onNumberChange: (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFormUpdate: (update: Partial<CommissionContract>) => void;
  onToggleItemReceived: () => void;
  onFileChange: (files: File[]) => void;
  onRemoveAttachment: (url: string) => void;
  onAddAddon: () => void;
  onRemoveAddon: (index: number) => void;
  onUpdateAddon: (
    index: number,
    field: "name" | "description" | "cost" | "currency",
    value: string | number
  ) => void;
}

export default function ItemTabSection({
  form,
  commission,
  isEditing,
  showNotes,
  showDamagesNotes,
  isUploading,
  onToggleNotes,
  onToggleDamagesNotes,
  onInputChange,
  onNumberChange,
  onFormUpdate,
  onToggleItemReceived,
  onFileChange,
  onRemoveAttachment,
  onAddAddon,
  onRemoveAddon,
  onUpdateAddon,
}: ItemTabSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="space-y-4">
      <SectionTitle text={t("store_commission_panel.item_data_title") || "Item data"} />
      <div className="mb-2">
        <InputShortText
          label={t("store_commission_panel.dress_model") || "Dress model"}
          defaultValue={isEditing ? form.dress : commission.dress}
          onChange={isEditing ? onInputChange("dress") : () => {}}
          isDisabled={!isEditing}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <InputShortText
          label={t("store_commission_panel.dress_size") || "Dress size"}
          defaultValue={isEditing ? form.dressSize : commission.dressSize || ""}
          onChange={isEditing ? onInputChange("dressSize") : () => {}}
          isDisabled={!isEditing}
        />
        <InputNumber
          label={t("store_commission_panel.purchase_year") || "Purchase year"}
          defaultValue={
            isEditing
              ? form.purchaseYear
                ? parseInt(form.purchaseYear)
                : undefined
              : commission.purchaseYear
                ? parseInt(commission.purchaseYear)
                : undefined
          }
          onChange={isEditing ? onNumberChange("purchaseYear") : () => {}}
          isDisabled={!isEditing}
          validationFn={(value) => value >= 1900 && value <= new Date().getFullYear()}
          errorMessage="errors.invalid_year"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <PriceField
          label={t("store_commission_panel.original_price") || "Original price"}
          currency={isEditing ? form.purchasePriceCurrency || "EUR" : commission.purchasePriceCurrency || "EUR"}
          price={isEditing ? form.purchasePriceAmount || 0 : commission.purchasePriceAmount || 0}
          currencies={CONFIG.SUPPORTED_CURRENCIES}
          onCurrencyChange={(currency) => onFormUpdate({ purchasePriceCurrency: currency })}
          onPriceChange={
            isEditing ? (price) => onFormUpdate({ purchasePriceAmount: price }) : () => {}
          }
          isDisabled={!isEditing}
        />
        <PriceField
          label={t("store_commission_panel.sale_price_range") || "Sale price range"}
          currency={isEditing ? form.desiredSalePriceCurrency || "EUR" : commission.desiredSalePriceCurrency || "EUR"}
          price={isEditing ? form.desiredSalePriceAmount || 0 : commission.desiredSalePriceAmount || 0}
          currencies={CONFIG.SUPPORTED_CURRENCIES}
          onCurrencyChange={(currency) => onFormUpdate({ desiredSalePriceCurrency: currency })}
          onPriceChange={
            isEditing ? (price) => onFormUpdate({ desiredSalePriceAmount: price }) : () => {}
          }
          isDisabled={!isEditing}
        />
      </div>

      <div className="mb-4">
        <button
          type="button"
          onClick={onToggleNotes}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline mb-2"
        >
          {showNotes ? (
            <>
              <ChevronUp className="h-4 w-4" />
              {t("store_commission_panel.hide_notes") || "Hide notes"}
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              {t("store_commission_panel.show_notes") || "Show notes"}
            </>
          )}
        </button>
        {showNotes && (
          <div className="mt-2">
            <TextArea
              label={t("store_commission_panel.dress_measurements") || "Dress measurements"}
              defaultValue={isEditing ? form.notes || "" : commission.notes || ""}
              onChange={
                isEditing ? (e) => onFormUpdate({ notes: e.target.value }) : () => {}
              }
              isDisabled={!isEditing}
              helperText={
                t("store_commission_panel.dress_measurements_helper") ||
                "Enter exact dress measurements to help match future clients"
              }
            />
          </div>
        )}
      </div>

      <div className="mb-4">
        <button
          type="button"
          onClick={onToggleDamagesNotes}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline mb-2"
        >
          {showDamagesNotes ? (
            <>
              <ChevronUp className="h-4 w-4" />
              {t("store_commission_panel.hide_damages_notes") || "Hide damages notes"}
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              {t("store_commission_panel.show_damages_notes") || "Show damages notes"}
            </>
          )}
        </button>
        {showDamagesNotes && (
          <div className="mt-2">
            <TextArea
              label={t("store_commission_panel.damages_notes") || "Damages Notes"}
              defaultValue={isEditing ? form.damagesNotes || "" : commission.damagesNotes || ""}
              onChange={
                isEditing ? (e) => onFormUpdate({ damagesNotes: e.target.value }) : () => {}
              }
              isDisabled={!isEditing}
              placeholder={
                t("store_commission_panel.damages_notes_placeholder") ||
                "Describe any damages or wear on the dress..."
              }
              maxLength={500}
            />
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center space-x-3">
          <Switch
            checked={isEditing ? form.itemReceived : commission.itemReceived}
            onChange={isEditing ? onToggleItemReceived : () => {}}
            disabled={!isEditing}
          />
          <label className="text-sm font-medium text-gray-700">
            {t("store_commission_panel.item_received") || "Item (dress) received"}
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t("store_commission_panel.item_received_helper") ||
            "Toggle this switch when the physical dress has been received by the store."}
        </p>
      </div>
      </div>

      <FilesTabSection
        form={form}
        commission={commission}
        isEditing={isEditing}
        isUploading={isUploading}
        onFileChange={onFileChange}
        onRemoveAttachment={onRemoveAttachment}
        onAddAddon={onAddAddon}
        onRemoveAddon={onRemoveAddon}
        onUpdateAddon={onUpdateAddon}
      />
    </div>
  );
}
